/**
 * Permissoes de staff (RN-F1).
 *
 * As permissoes sao fixas no codigo, porque cada uma protege endpoints concretos.
 * Os papeis (Administrador geral, Monitor, ...) sao dados na base `identity` e
 * juntam permissoes: criar um papel novo nao exige mexer aqui.
 *
 * Esta lista tem de bater certo com o `INSERT INTO permission` de
 * `db/identity/migrations`. Uma permissao nova = uma migracao + uma linha aqui.
 */
export const PERMISSIONS = {
  'staff.manage': { arenaScoped: false },
  'members.view': { arenaScoped: false },
  'members.view_minors': { arenaScoped: false },
  'members.approve': { arenaScoped: false },
  'members.manage': { arenaScoped: false },
  'members.register_youth': { arenaScoped: true },
  'arena.monitor': { arenaScoped: true },
  'arena.manage': { arenaScoped: false },
  'store.manage': { arenaScoped: false },
  'content.manage': { arenaScoped: false },
  'events.manage': { arenaScoped: false },
  'events.budget': { arenaScoped: false },
  'streaming.manage': { arenaScoped: false },
  'streaming.padrinho': { arenaScoped: false },
  'streaming.channel': { arenaScoped: false },
  'giveaways.manage': { arenaScoped: false },
  'contacts.manage': { arenaScoped: false },
  'contacts.direcao': { arenaScoped: false },
  'partners.manage': { arenaScoped: false },
  'social.manage': { arenaScoped: false },
  'social.create': { arenaScoped: false },
  'teams.manage': { arenaScoped: false },
  'teams.own': { arenaScoped: false },
  'tournaments.manage': { arenaScoped: false },
} as const satisfies Record<string, { arenaScoped: boolean }>;

export type Permission = keyof typeof PERMISSIONS;

export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as Permission[];

/**
 * Forma de uma concessao no token de acesso: `"<permissao>"` vale para tudo;
 * `"<permissao>@<arenaId>"` so para essa arena.
 */
export type Grant = Permission | `${Permission}@${string}`;

export function formatGrant(permission: Permission, arenaId: string | null): Grant {
  return arenaId === null ? permission : `${permission}@${arenaId}`;
}

/**
 * `true` se a lista de concessoes cobre `permission` (e, quando dada, a arena).
 * Uma concessao sem arena cobre todas as arenas.
 */
export function hasGrant(
  grants: readonly string[],
  permission: Permission,
  arenaId?: string | null,
): boolean {
  if (grants.includes(permission)) return true;
  if (arenaId === undefined || arenaId === null) return false;
  return grants.includes(`${permission}@${arenaId}`);
}

/** `true` se a lista tem `permission` em pelo menos uma arena (ou em todas). */
export function hasGrantAnywhere(grants: readonly string[], permission: Permission): boolean {
  return grants.some((g) => g === permission || g.startsWith(`${permission}@`));
}
